// Persistent village progression. Static definitions are shared by UI and server.
import type { Resource, Stockpile } from './types'

export type Improvement = 'harvest' | 'builders' | 'craft'
export type ExpeditionKind = 'forage' | 'ruins' | 'bandits'
export interface Discovery { serial: number; kind: ExpeditionKind; q: number; r: number }
export type ExpeditionSites = Partial<Record<ExpeditionKind, { q: number; r: number }>>
export interface Expedition {
  serial: number
  kind: ExpeditionKind
  q: number
  r: number
  party: number[]
  guards: number[]
  started: number
}
export interface RealmProgress {
  version: 1
  born: number
  renown: number
  earned: number
  claimed: string[]
  improvements: Partial<Record<Improvement, number>>
  produced: Partial<Stockpile>
  crafted: number
  firstHammerMade?: boolean
  expeditionsWon: number
  serial: number
  expedition?: Expedition
  nextExpedition: number
  festivalUntil: number
  nextFestival: number
  sites?: ExpeditionSites
  discovery?: Discovery
  relics?: Partial<Record<ExpeditionKind, number>>
}
export interface RealmView {
  firstHammerMade?: boolean
  renown: number
  earned: number
  claimed: string[]
  improvements: Partial<Record<Improvement, number>>
  progress: Record<string, number>
  expeditionsWon: number
  nextExpedition: number
  expedition?: Omit<Expedition, 'guards'> & { guardsLeft: number; partyLeft: number; distanceLeft?: number }
  festivalUntil: number
  nextFestival: number
  sites?: ExpeditionSites
  discovery?: Discovery
  relics?: Partial<Record<ExpeditionKind, number>>
}
export const RELICS: Record<ExpeditionKind, { name: string; story: string; bonus: string; salvage: Partial<Stockpile> }> = {
  forage: { name: 'Silverleaf seeds', story: 'Beyond the meadow, your scouts find a luminous garden that has survived without a keeper. A handful of its seeds could change every harvest back home.', bonus: '+10% farm and fishing output', salvage: { food: 65, wood: 25 } },
  ruins: { name: 'The mason’s compass', story: 'Under a fallen arch lies a brass compass. Its needle points to the strongest stone. Your builders could learn its secrets, or salvage the surrounding stone and metal for supplies.', bonus: '+10% construction speed', salvage: { stone: 55, gold: 20 } },
  bandits: { name: 'The embersteel anvil', story: 'The raiders guarded a stolen anvil that is still warm to the touch. Restore it to your workshops, or melt down the cache for immediate wealth.', bonus: '+15% crafting speed', salvage: { iron: 40, gold: 40 } },
}
export const RELIC_MAX = 3
export const EXPEDITION_RADIUS: Record<ExpeditionKind, number> = { forage: 4, ruins: 6, bandits: 8 }
export interface Reward { renown: number; goods: Partial<Stockpile> }
export const MILESTONES = [
  { id: 'shelter', title: 'A place to belong', hint: 'Finish your first house. Your hammer-holder builds while empty-handed settlers deliver materials.', metric: 'house', goal: 1, reward: { renown: 15, goods: { wood: 25, food: 20 } } },
  { id: 'table', title: 'Set the table', hint: 'Finish a farm or fishing pier. Farms need a hoe; fishing needs a rod.', metric: 'foodBuildings', goal: 1, reward: { renown: 15, goods: { food: 35, wood: 20 } } },
  { id: 'tools', title: 'Made by our own hands', hint: 'Make a tool at the Town Hall. Automatic management orders the tools your village needs.', metric: 'crafted', goal: 1, reward: { renown: 15, goods: { wood: 30 } } },
  { id: 'crew', title: 'Everyone has a part', hint: 'Have two people working at finished production buildings. The Village Council can handle staffing and tools.', metric: 'working', goal: 2, reward: { renown: 20, goods: { stone: 25 } } },
  { id: 'stores', title: 'Room to prosper', hint: 'Finish a storehouse near your industry to shorten hauling trips.', metric: 'storehouse', goal: 1, reward: { renown: 20, goods: { wood: 25, gold: 10 } } },
  { id: 'harvest', title: 'The first harvest', hint: 'Produce 30 food on farms or piers. Only food made by your workers counts.', metric: 'foodProduced', goal: 30, reward: { renown: 25, goods: { gold: 15 } } },
  { id: 'watch', title: 'A watch of our own', hint: 'Equip three people with weapons to defend your settlement and explorers.', metric: 'armed', goal: 3, reward: { renown: 30, goods: { iron: 20 } } },
  { id: 'forge', title: 'Strike while it is hot', hint: 'Finish a Smithy. A Workbench can make the smith’s hammer its crew needs.', metric: 'barracks', goal: 1, reward: { renown: 35, goods: { iron: 25, gold: 15 } } },
  { id: 'town', title: 'From village to town', hint: 'Reach 20 people. Keep food above zero and build houses for new arrivals.', metric: 'population', goal: 20, reward: { renown: 50, goods: { stone: 50, gold: 25 } } },
] satisfies { id: string; title: string; hint: string; metric: string; goal: number; reward: Reward }[]

export const IMPROVEMENTS: Record<Improvement, { name: string; icon: string; desc: string; resource: Resource }> = {
  harvest: { name: 'Crop rotation', icon: '🌾', desc: '+20% farm and fishing output per level.', resource: 'wood' },
  builders: { name: 'Master builders', icon: '🔨', desc: '+20% building construction speed per level. Material costs stay the same.', resource: 'stone' },
  craft: { name: 'Better workbenches', icon: '⚒', desc: '+25% tool and weapon crafting speed per level. Material costs stay the same.', resource: 'wood' },
}
export const MAX_IMPROVEMENT = 3
export function improvementCost(key: Improvement, level: number): Reward {
  return { renown: [25, 45, 70][level] ?? 70, goods: { [IMPROVEMENTS[key].resource]: [20, 40, 65][level] ?? 65 } }
}
export const EXPEDITIONS: Record<ExpeditionKind, { name: string; icon: string; desc: string; party: number; guards: number; cost: Partial<Stockpile>; reward: Reward }> = {
  forage: { name: 'The hidden meadow', icon: '🌿', desc: 'Send one idle scout to gather wild provisions. No added enemies; ordinary wildlife still lives here.', party: 1, guards: 0, cost: { food: 8 }, reward: { renown: 8, goods: { food: 45, wood: 15 } } },
  ruins: { name: 'The forgotten quarry', icon: '🗿', desc: 'Send two settlers to salvage stone and old iron. A longer trip, with no added enemies.', party: 2, guards: 0, cost: { food: 15 }, reward: { renown: 14, goods: { stone: 35, iron: 10, gold: 5 } } },
  bandits: { name: 'The bandit cache', icon: '⚔', desc: 'Send at least three armed people against two raiders. People can die. Defeat both guards and reach the cache.', party: 3, guards: 2, cost: { food: 25 }, reward: { renown: 30, goods: { gold: 25, iron: 20, wood: 30 } } },
}
export function realmRank(earned: number): { name: string; next: number | null } {
  if (earned >= 500) return { name: 'Free City', next: null }
  if (earned >= 250) return { name: 'Borough', next: 500 }
  if (earned >= 120) return { name: 'Township', next: 250 }
  if (earned >= 45) return { name: 'Village', next: 120 }
  return { name: 'Hamlet', next: 45 }
}
export function realmMultiplier(p: Pick<RealmProgress, 'improvements' | 'festivalUntil' | 'relics'> | undefined, type: Improvement, tick: number): number {
  const level = Math.max(0, Math.min(MAX_IMPROVEMENT, p?.improvements[type] ?? 0))
  return 1 + level * (type === 'craft' ? 0.25 : 0.2)
    + Math.max(0, Math.min(RELIC_MAX, p?.relics?.[type === 'harvest' ? 'forage' : type === 'builders' ? 'ruins' : 'bandits'] ?? 0)) * (type === 'craft' ? 0.15 : 0.1)
    + (type === 'harvest' && (p?.festivalUntil ?? 0) > tick ? 0.25 : 0)
}
